import { useEffect, useState } from 'react';
import { fetchPaymentMode, startKakaoPayment, startNaverPayment } from '../../services/paymentService';
import styles from './PaymentPanel.module.scss';

interface PaymentPanelProps {
  orderNo: string;
  orderName: string;
  amount: number;
  customerName: string;
  // 결제 금액 위 안내 문구 (기본: 시안 최종 승인 후 결제)
  description?: string;
}

type Provider = 'kakao' | 'naver';

const DEFAULT_DESCRIPTION = '시안이 최종 승인되었습니다. 결제수단을 선택하고 결제를 진행해주세요.';

function PaymentPanel({ orderNo, amount, description = DEFAULT_DESCRIPTION }: PaymentPanelProps) {
  const [error, setError] = useState<string | null>(null);
  const [processingProvider, setProcessingProvider] = useState<Provider | null>(null);
  const [isTestMode, setIsTestMode] = useState(false);

  // 서버가 테스트 모드(PAYMENT_TEST_MODE)면 실제 청구되지 않는다는 안내를 띄운다
  useEffect(() => {
    fetchPaymentMode()
      .then(({ testMode }) => setIsTestMode(testMode))
      .catch(() => setIsTestMode(false));
  }, []);

  const handleKakaoPay = async () => {
    setError(null);
    setProcessingProvider('kakao');
    try {
      const { redirectUrl } = await startKakaoPayment(orderNo);
      window.location.href = redirectUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : '카카오페이 결제 진행 중 오류가 발생했습니다.');
      setProcessingProvider(null);
    }
  };

  const handleNaverPay = async () => {
    setError(null);
    setProcessingProvider('naver');
    try {
      // 테스트 주문이면 서버가 development 모드를 내려준다
      const { mode, clientId, chainId, payParams } = await startNaverPayment(orderNo);
      if (!window.Naver?.Pay) {
        throw new Error('네이버페이 결제 모듈을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
      }
      // 기본값인 팝업은 await 뒤에 열려서 사용자 클릭으로 인정되지 않아 브라우저가 차단한다. 현재 창에서 연다
      const naverPay = window.Naver.Pay.create({ mode, clientId, chainId, openType: 'page' });
      naverPay.open(payParams);
    } catch (err) {
      setError(err instanceof Error ? err.message : '네이버페이 결제 진행 중 오류가 발생했습니다.');
    } finally {
      setProcessingProvider(null);
    }
  };

  return (
    <div className={styles.panel}>
      <span className={styles.eyebrow}>결제하기</span>
      {isTestMode && (
        <p className={styles.testNotice}>테스트 결제 모드입니다. 결제를 진행해도 실제로 청구되지 않습니다.</p>
      )}
      <p className={styles.desc}>{description}</p>
      <p className={styles.amount}>₩ {amount.toLocaleString()}</p>

      <div className={styles.methodRow}>
        <button
          type="button"
          className={`${styles.payButton} ${styles.kakaoButton}`}
          onClick={handleKakaoPay}
          disabled={processingProvider !== null}
        >
          {processingProvider === 'kakao' ? '결제창 여는 중...' : '카카오페이로 결제'}
        </button>
        <button
          type="button"
          className={`${styles.payButton} ${styles.naverButton}`}
          onClick={handleNaverPay}
          disabled={processingProvider !== null}
        >
          {processingProvider === 'naver' ? '결제창 여는 중...' : '네이버페이로 결제'}
        </button>
      </div>

      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

export default PaymentPanel;
